import { format, parseISO, parse, isValid } from 'date-fns'
import { tr } from 'date-fns/locale'
import { Download, Paperclip, Star } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { fmtRelative } from '@/features/shared/format'
import { apiErrorMessage } from '@/i18n'
import { downloadFile } from '@/lib/api/client'
import { cn } from '@/lib/utils'
import { formatBytes } from '../mailAttachments'
import type { MailMessage } from '../mailTypes'
import { MailBodyView } from './MailBodyView'

type Props = {
  messages: MailMessage[]
  threadKey: string
  onToggleFavorite: (id: string) => void
}

function fmtThreadStamp(dateString: string) {
  const isTurkishDateFormat = /^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}:\d{2}$/.test(dateString)

  const date = isTurkishDateFormat
    ? parse(dateString, 'dd.MM.yyyy HH:mm:ss', new Date())
    : parseISO(dateString)

  if (!isValid(date)) return dateString

  return format(date, 'd MMM EEE HH:mm', { locale: tr })
}

/** Gmail tarzı katlanır konuşma: satırda özet, tıklayınca tam içerik. */
export function MailThreadView({ messages, threadKey, onToggleFavorite }: Props) {
  return <ThreadPanel key={threadKey} messages={messages} onToggleFavorite={onToggleFavorite} />
}

function ThreadPanel({
  messages,
  onToggleFavorite,
}: {
  messages: MailMessage[]
  onToggleFavorite: (id: string) => void
}) {
  const { t } = useTranslation()
  const lastId = messages[messages.length - 1]?.id ?? ''
  const [expandedIds, setExpandedIds] = useState(() => new Set(lastId ? [lastId] : []))

  function toggle(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="mt-3 divide-y divide-border rounded-lg border border-border bg-card">
      {messages.map((m) => {
        const expanded = expandedIds.has(m.id)
        const displayName = m.from_name ?? m.from
        const snippet = m.snippet || ''

        return (
          <div key={m.id} className={cn('bg-card', expanded && 'bg-muted/20')}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggle(m.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggle(m.id)
                }
              }}
              className={cn(
                'flex w-full cursor-pointer items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-muted/40',
                expanded && 'hover:bg-transparent',
              )}
              aria-expanded={expanded}
            >
              <Avatar name={displayName} className="mt-0.5 size-9 shrink-0" />

              <div className="min-w-0 flex-1">
                {expanded ? (
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-sm font-semibold text-foreground">{displayName}</span>
                      <span className="text-xs text-muted-foreground">&lt;{m.from}&gt;</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('mail.mailbox.to')}: {m.to.join(', ')}
                      {m.cc && m.cc.length > 0 ? ` · ${t('mail.mailbox.cc')}: ${m.cc.join(', ')}` : ''}
                      {m.bcc && m.bcc.length > 0 ? ` · ${t('mail.mailbox.bcc')}: ${m.bcc.join(', ')}` : ''}
                    </p>
                  </div>
                ) : (
                  <p className="truncate text-sm leading-snug">
                    <span className="font-semibold text-foreground">{displayName}</span>
                    <span className="text-muted-foreground"> {snippet}</span>
                  </p>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
                {(m.attachments?.length ?? 0) > 0 ? (
                  <Paperclip className="size-3.5 text-muted-foreground" aria-hidden />
                ) : null}
                <time
                  className="whitespace-nowrap text-xs text-muted-foreground"
                  dateTime={m.created_at}
                  title={fmtThreadStamp(m.created_at)}
                >
                  {fmtThreadStamp(m.created_at)}
                  <span className="ml-1 hidden sm:inline">({fmtRelative(m.created_at)})</span>
                </time>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={m.is_favorite ? t('mail.mailbox.unstar') : t('mail.mailbox.star')}
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleFavorite(m.id)
                  }}
                >
                  <Star className={cn('size-3.5', m.is_favorite && 'fill-warning text-warning')} />
                </Button>
              </div>
            </div>

            {expanded ? (
              <div className="px-3 pb-3 pl-12 sm:px-4 sm:pl-14">
                <div className="text-[15px] leading-relaxed text-foreground">
                  <MailBodyView html={m.body_html} />
                </div>
                {m.attachments && m.attachments.length > 0 ? (
                  <ul className="mt-3 space-y-1">
                    {m.attachments.map((a) => (
                      <li
                        key={a.id}
                        className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5 text-xs"
                      >
                        <Paperclip className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                        <span className="min-w-0 flex-1 truncate font-medium">{a.name}</span>
                        <span className="text-muted-foreground">{formatBytes(a.size)}</span>
                        {a.downloadable ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-7"
                            aria-label={t('mail.mailbox.attachments.download', { name: a.name })}
                            onClick={() =>
                              void downloadFile(`/api/v1/mail/messages/${m.id}/attachments/${a.id}`, a.name).catch((e: unknown) => toast.error(apiErrorMessage(e)))
                            }
                          >
                            <Download />
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
